import {Nullable} from "../../models/common/Nullable";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {Timesheet} from "../../models/timesheet/Timesheet";
import {
    CalendarViewActionTypes,
    FETCH_CALENDAR_ENTRIES,
    FETCH_CALENDAR_ENTRIES_ERROR,
    FETCH_CALENDAR_ENTRIES_SUCCESS,
    FETCH_HOLIDAYS,
    FETCH_HOLIDAYS_ERROR,
    FETCH_HOLIDAYS_SUCCESS,
    FETCH_MANDATORY_WORKHOURS,
    FETCH_MANDATORY_WORKHOURS_ERROR,
    FETCH_MANDATORY_WORKHOURS_SUCCESS,
    FETCH_WORKDAYS, FETCH_WORKDAYS_ERROR,
    FETCH_WORKDAYS_SUCCESS
} from "../../actions/calendar/CalendarActions";
import {getWeekRange} from "../../helpers/DateTimeHelpers";
import {DateRange} from "../../models/common/DateRange";
import {addDays, differenceInMinutes} from "date-fns";
import {WorkingHours} from "../../models/timesheet/WorkingHours";
import {Workday, Workweek} from "../../models/calendar/Workday";
import {Holiday, HolidayMap, dateMapKey} from "../../models/calendar/Holiday";
import {compare} from "../../helpers/Sort";
import {DateMap} from "../../models/calendar/Calendar";


export interface CalendarStateTimePeriodItem {
    entries: Nullable< Timesheet[] >
    request: RequestState
    dateRange: DateRange
}

export interface CalendarState {
    /** the timesheet items of the currently displayed calendar */
    currentTimePeriod: CalendarStateTimePeriodItem
    /** preloaded the time sheet items for the next displayable time period */
    nextTimePeriod: CalendarStateTimePeriodItem
    holidays: {
        entries: Nullable< HolidayMap >
        dateRange: DateRange
        request: RequestState
    }
    workhours: {
        /** workhours are kept in state as minute  */
        total: number
        byDate: DateMap< number >
    }
    mandatoryHours: {
        entries: Nullable< WorkingHours >
        dateRange: DateRange
        request: RequestState
    }
    workdays: {
        entries: Nullable< Workweek >
        request: RequestState
    }
}

const today = new Date();
const initialRequestState = getInitialRequestState();
const currentWeekRange = getWeekRange( today );

const initialState: CalendarState = {
    currentTimePeriod: {
        entries: null,
        request: initialRequestState,
        dateRange: currentWeekRange
    } ,
    nextTimePeriod: {
        entries: null,
        request: initialRequestState,
        dateRange: getWeekRange( addDays( today, 7 ) )
    },
    workhours: {
        total: 0,
        byDate: {}
    },
    mandatoryHours: {
        entries: null,
        dateRange: currentWeekRange,
        request: initialRequestState
    },
    workdays: {
        entries: null,
        request: initialRequestState
    },
    holidays: {
        entries: null,
        request: initialRequestState,
        dateRange: currentWeekRange
    }
};

export function CalendarReducer(
    state: CalendarState = initialState,
    action: CalendarViewActionTypes
) {

    switch( action.type ) {
        case FETCH_CALENDAR_ENTRIES:
            return {
                ...state,
                currentTimePeriod: {
                    ...state.currentTimePeriod,
                    dateRange: action.dateRange,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_CALENDAR_ENTRIES_SUCCESS:
            return {
                ...state,
                currentTimePeriod: {
                    entries: action.payload.entries,
                    request: successFetchingRequestState()
                },
                workhours: {
                    total: action.payload.entries.reduce(
                        ( _aggre, _timesheet ) =>
                            _aggre + differenceInMinutes( _timesheet.finishAt, _timesheet.startAt ),
                        0
                    ),
                    byDate: calcWorkhoursByDate( action.payload.entries )
                }
            };
        case FETCH_CALENDAR_ENTRIES_ERROR:
            return {
                ...state,
                currentTimePeriod: {
                    ...state.currentTimePeriod,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_HOLIDAYS:
            return {
                ...state,
                holidays: {
                    ...state.holidays,
                    dateRange: action.dateRange,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_HOLIDAYS_SUCCESS:
            //`${ _holiday.day }_${ _holiday.month }_${ _holiday.year }`
            return {
                ...state,
                holidays: {
                    ...state.holidays,
                    entries: action.payload.reduce(( _aggre, _holiday ) => {
                        const date = new Date( _holiday.date );
                        return {
                            ..._aggre,
                            [ dateMapKey( date ) ]: _holiday
                        };
                    }, {}),
                    request: successFetchingRequestState()
                }
            };
        case FETCH_HOLIDAYS_ERROR:
            return {
                ...state,
                holidays: {
                    ...state.holidays,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_MANDATORY_WORKHOURS:
            return {
                ...state,
                mandatoryHours: {
                    ...state.mandatoryHours,
                    dateRange: action.dateRange,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_MANDATORY_WORKHOURS_SUCCESS:
            return {
                ...state,
                mandatoryHours: {
                    ...state.mandatoryHours,
                    entries: action.payload,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_MANDATORY_WORKHOURS_ERROR:
            return {
                ...state,
                mandatoryHours: {
                    ...state.mandatoryHours,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_WORKDAYS:
            return {
                ...state,
                workdays: {
                    ...state.workdays,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_WORKDAYS_SUCCESS:
            return {
                ...state,
                workdays: {
                    ...state.workdays,
                    entries: action.payload.reduce( ( _aggre, _workday ) => ({
                        ..._aggre,
                        [( typeof _workday.dayOfWeek === "string" ) ? parseInt( _workday.dayOfWeek ) : _workday.dayOfWeek ]: _workday
                    }), {} ),
                    request: successFetchingRequestState()
                }
            };
        case FETCH_WORKDAYS_ERROR:
            return {
                ...state,
                workdays: {
                    ...state.workdays,
                    request: errorFetchingRequestState( action.error )
                }
            };
        default:
            return state;
    }
}

function calcWorkhoursByDate( timesheetEntries: Timesheet[] ): DateMap< number > {
    let dateMap: DateMap< number > = {};

    timesheetEntries.forEach( _timesheet => {
        const date = new Date( _timesheet.startAt );
        const mapKey = dateMapKey( date );

        if( typeof dateMap[ mapKey ] === 'undefined' ) {
            dateMap = {
                ...dateMap,
                [ mapKey ]: differenceInMinutes( _timesheet.finishAt, _timesheet.startAt )
            };
        } else {
            dateMap = {
                ...dateMap,
                [ mapKey ]: dateMap[ mapKey ] + differenceInMinutes( _timesheet.finishAt, _timesheet.startAt)
            };
        }
    });

    return dateMap;
}